import React from 'react'
import {
  Alert,
  Box,
  IconButton,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import UndoIcon from '@mui/icons-material/Undo'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import PrepGrid from './PrepGrid'
import PrepListViewport from './PrepListViewport'
import PrepCard from './PrepCard'
import usePrepRecoveryStore, { type PrepRecoveryMode } from '../../store/prepRecoveryStore'
import usePrepListStore from '../../store/prepListStore'
import usePrepDashboardStore from '../../store/prepDashboardStore'
import useUserSettingsStore from '../../store/userSettings'
import useStationStore from '../../store/station'
import {
  addPrepItemToMyPrep,
  fetchPrepRecovery,
  restoreDismissForAll,
  undoDismissPrepItem,
  undoPrepUsed,
  type PrepCategoryWithCount,
  type PrepListItem,
} from '../../api/prep'
import { sanitizePrepListItem } from '../../utils/prepItemNormalization'
import { formatRelativeTime } from '../../utils/relativeTime'
import { resolveEpochMs } from '../../utils/time'

interface PrepRecoveryShellProps {
  isSuperadmin: boolean
  forceSingleColumn?: boolean
  onNotify?: (message: string, severity: 'success' | 'error') => void
  onCategoriesUpdate?: (categories: PrepCategoryWithCount[]) => void
  onCategoriesLoading?: () => void
}

const PAGE_SIZE = 30

const PrepRecoveryShell: React.FC<PrepRecoveryShellProps> = ({
  isSuperadmin,
  forceSingleColumn = false,
  onNotify,
  onCategoriesUpdate,
  onCategoriesLoading,
}) => {
  const recoveryMode = usePrepRecoveryStore((state) => state.mode)
  const setRecoveryMode = usePrepRecoveryStore((state) => state.setMode)
  const selectedCategories = usePrepListStore((state) => state.selectedCategories)
  const categoryLogic = usePrepListStore((state) => state.mode)
  const search = usePrepListStore((state) => state.search)
  const lineClamp = usePrepListStore((state) => state.lineClamp)
  const updateMyItem = usePrepListStore((state) => state.updateMyItem)
  const ageMaxDays = useUserSettingsStore((state) => state.prep.ageMaxDays)
  const stationId = useStationStore((state) => state.activeStation?.id ?? 0)
  const stationTimezone = useStationStore((state) => state.activeStation?.timezone)

  const [itemsState, setItemsState] = React.useState<{
    items: PrepListItem[]
    loading: boolean
    loadingMore: boolean
    error: string | null
    page: number
    total: number
    hasMore: boolean
  }>(() => ({
    items: [],
    loading: true,
    loadingMore: false,
    error: null,
    page: 1,
    total: 0,
    hasMore: false,
  }))

  const listRequestRef = React.useRef<AbortController | null>(null)
  const [pendingActionIds, setPendingActionIds] = React.useState<Set<number>>(() => new Set())

  const setItemPending = React.useCallback((itemId: number, pending: boolean) => {
    setPendingActionIds((prev) => {
      const next = new Set(prev)
      if (pending) {
        next.add(itemId)
      } else {
        next.delete(itemId)
      }
      return next
    })
  }, [])

  React.useEffect(() => {
    if (!isSuperadmin && recoveryMode === 'dismissed_all') {
      setRecoveryMode('dismissed')
    }
  }, [isSuperadmin, recoveryMode, setRecoveryMode])

  const normalizeItems = React.useCallback((items: unknown): PrepListItem[] => {
    if (!Array.isArray(items)) {
      return []
    }

    const normalized: PrepListItem[] = []

    for (const entry of items) {
      const inMyPrep = Boolean((entry as { is_in_my_prep?: unknown })?.is_in_my_prep)
      const sanitized = sanitizePrepListItem(entry, { inMyPrep })
      if (sanitized) {
        normalized.push(sanitized)
      }
    }

    return normalized
  }, [])

  const normalizeCategories = React.useCallback((entries: unknown): PrepCategoryWithCount[] => {
    if (!Array.isArray(entries)) {
      return []
    }

    const normalized: PrepCategoryWithCount[] = []

    for (const entry of entries) {
      const id = entry && typeof entry === 'object' && 'id' in entry ? Number(entry.id) : Number.NaN
      const slug = entry && typeof entry === 'object' && 'slug' in entry ? String(entry.slug ?? '') : ''
      if (!Number.isFinite(id) || id <= 0 || !slug) {
        continue
      }
      const name = entry && typeof entry === 'object' && 'name' in entry ? String(entry.name ?? '') : ''
      const count =
        entry && typeof entry === 'object' && 'count' in entry && typeof entry.count === 'number'
          ? Math.max(0, entry.count)
          : 0
      normalized.push({ id, slug, name, count })
    }

    return normalized
  }, [])

  const updateRecoveryItem = React.useCallback((itemId: number, patch: Partial<PrepListItem>) => {
    setItemsState((prev) => {
      const nextItems = prev.items.map((entry) => (entry.id === itemId ? { ...entry, ...patch } : entry))
      return {
        ...prev,
        items: nextItems,
      }
    })
  }, [])

  const removeRecoveryItem = React.useCallback((itemId: number) => {
    setItemsState((prev) => {
      const nextItems = prev.items.filter((entry) => entry.id !== itemId)
      if (nextItems.length === prev.items.length) {
        return prev
      }
      const nextTotal = prev.total > 0 ? prev.total - 1 : 0
      const nextHasMore = prev.page * PAGE_SIZE < nextTotal
      return {
        ...prev,
        items: nextItems,
        total: nextTotal,
        hasMore: nextHasMore,
      }
    })
  }, [])

  const resolveErrorMessage = React.useCallback((error: unknown, fallback: string): string => {
    if (error && typeof error === 'object' && 'message' in error) {
      const message = (error as { message?: unknown }).message
      if (typeof message === 'string' && message.trim()) {
        return message
      }
    }
    return fallback
  }, [])

  const fetchItems = React.useCallback(
    (append: boolean, pageOverride?: number) => {
      if (!stationId) {
        return
      }

      if (listRequestRef.current) {
        listRequestRef.current.abort()
      }

      const controller = new AbortController()
      listRequestRef.current = controller

      setItemsState((prev) => ({
        ...prev,
        loading: append ? prev.loading : true,
        loadingMore: append,
        error: null,
        items: append ? prev.items : [],
      }))

      if (!append) {
        onCategoriesLoading?.()
      }

      const trimmedSearch = search.trim()
      const searching = trimmedSearch.length > 0
      const targetPage = pageOverride ?? 1

      const params = {
        stationId,
        mode: recoveryMode,
        categories: selectedCategories,
        logic: categoryLogic === 'and' ? 'and' : 'or',
        limit: PAGE_SIZE,
        page: targetPage,
      } as const

      const requestParams = { ...params } as {
        stationId: number
        mode: PrepRecoveryMode
        categories?: string[]
        logic?: 'and' | 'or'
        q?: string
        age_max_days?: number
        page?: number
        limit?: number
      }

      if (searching) {
        requestParams.q = trimmedSearch
      } else if (typeof ageMaxDays === 'number' && ageMaxDays > 0) {
        requestParams.age_max_days = ageMaxDays
      }

      fetchPrepRecovery(requestParams, { signal: controller.signal })
        .then((response) => {
          const normalizedItems = normalizeItems(response.items)
          if (!append) {
            const normalizedCategories = normalizeCategories(response.categories)
            onCategoriesUpdate?.(normalizedCategories)
          }
          const normalizedPage =
            typeof response.page === 'number' && Number.isFinite(response.page)
              ? Math.max(1, Math.floor(response.page))
              : targetPage
          const normalizedPerPage =
            typeof response.per_page === 'number' && Number.isFinite(response.per_page)
              ? Math.max(1, Math.floor(response.per_page))
              : PAGE_SIZE
          const normalizedTotal =
            typeof response.total === 'number' && Number.isFinite(response.total)
              ? Math.max(0, Math.floor(response.total))
              : normalizedItems.length
          const nextHasMore = normalizedPage * normalizedPerPage < normalizedTotal

          setItemsState((prev) => ({
            items: append ? [...prev.items, ...normalizedItems] : normalizedItems,
            loading: false,
            loadingMore: false,
            error: null,
            page: normalizedPage,
            total: normalizedTotal,
            hasMore: nextHasMore,
          }))
        })
        .catch((error) => {
          if (controller.signal.aborted) {
            return
          }
          const message =
            error && typeof error === 'object' && 'message' in error
              ? String((error as { message?: unknown }).message ?? 'Failed to load prep items.')
              : 'Failed to load prep items.'

          setItemsState((prev) => ({
            ...prev,
            loading: false,
            loadingMore: false,
            error: message,
          }))
        })
        .finally(() => {
          if (listRequestRef.current === controller) {
            listRequestRef.current = null
          }
        })
    },
    [
      ageMaxDays,
      categoryLogic,
      normalizeCategories,
      normalizeItems,
      onCategoriesLoading,
      onCategoriesUpdate,
      recoveryMode,
      search,
      selectedCategories,
      stationId,
    ],
  )

  React.useEffect(() => {
    if (!stationId) {
      return
    }
    fetchItems(false, 1)
  }, [fetchItems, stationId])

  React.useEffect(
    () => () => {
      listRequestRef.current?.abort()
      listRequestRef.current = null
    },
    [],
  )

  const loadMore = React.useCallback(() => {
    if (itemsState.loading || itemsState.loadingMore || !itemsState.hasMore) {
      return
    }
    fetchItems(true, itemsState.page + 1)
  }, [fetchItems, itemsState.hasMore, itemsState.loading, itemsState.loadingMore, itemsState.page])

  const performUndo = React.useCallback(
    async (item: PrepListItem) => {
      if (recoveryMode === 'dismissed_all') {
        await restoreDismissForAll(item.id)
        return
      }

      if (recoveryMode === 'dismissed') {
        await undoDismissPrepItem(item.id)
        return
      }

      const usedDate = item.recovery?.used_date
      if (!stationId) {
        throw new Error('No station selected.')
      }
      if (!usedDate) {
        throw new Error('Unable to undo used item without a used date.')
      }
      await undoPrepUsed(stationId, item.id, usedDate)
    },
    [recoveryMode, stationId],
  )

  const handleUndo = React.useCallback(
    async (item: PrepListItem) => {
      if (pendingActionIds.has(item.id)) {
        return
      }
      setItemPending(item.id, true)
      try {
        await performUndo(item)
        usePrepDashboardStore.getState().markSnapshotDirty()
        removeRecoveryItem(item.id)
        const label = item.headline?.trim() ? `"${item.headline.trim()}"` : 'Prep item'
        const message =
          recoveryMode === 'used'
            ? `${label} marked as not used.`
            : recoveryMode === 'dismissed_all'
              ? `${label} restored for everyone.`
              : `${label} restored.`
        onNotify?.(message, 'success')
      } catch (error) {
        onNotify?.(resolveErrorMessage(error, 'Unable to restore this prep item right now.'), 'error')
      } finally {
        setItemPending(item.id, false)
      }
    },
    [onNotify, pendingActionIds, performUndo, recoveryMode, removeRecoveryItem, resolveErrorMessage, setItemPending],
  )

  const handleAddToMyPrepAndUndo = React.useCallback(
    async (item: PrepListItem) => {
      if (pendingActionIds.has(item.id) || item.is_in_my_prep) {
        return
      }
      setItemPending(item.id, true)
      try {
        await addPrepItemToMyPrep(item.id)
        updateMyItem(item, true)
        updateRecoveryItem(item.id, { is_in_my_prep: true })
        await performUndo(item)
        usePrepDashboardStore.getState().markSnapshotDirty()
        removeRecoveryItem(item.id)
        const label = item.headline?.trim() ? `"${item.headline.trim()}"` : 'Prep item'
        onNotify?.(`${label} added to My Prep and restored.`, 'success')
      } catch (error) {
        onNotify?.(resolveErrorMessage(error, 'Unable to update this prep item right now.'), 'error')
      } finally {
        setItemPending(item.id, false)
      }
    },
    [
      onNotify,
      pendingActionIds,
      performUndo,
      removeRecoveryItem,
      resolveErrorMessage,
      setItemPending,
      updateMyItem,
      updateRecoveryItem,
    ],
  )

  const handleModeChange = React.useCallback(
    (_event: React.SyntheticEvent<Element, Event>, next: PrepRecoveryMode | null) => {
      if (!next) {
        return
      }
      setRecoveryMode(next)
    },
    [setRecoveryMode],
  )

  const emptyMessage = React.useMemo(() => {
    if (recoveryMode === 'used') {
      return 'No used prep items match your filters.'
    }
    if (recoveryMode === 'dismissed_all') {
      return 'No items have been dismissed for everyone.'
    }
    return 'No dismissed prep items match your filters.'
  }, [recoveryMode])

  const actionsSlot = React.useCallback(
    (item: PrepListItem) => {
      const canStar = recoveryMode !== 'dismissed_all'
      const isStarred = Boolean(item.is_in_my_prep)
      const isPending = pendingActionIds.has(item.id)
      const missingUsedDate = recoveryMode === 'used' && !item.recovery?.used_date
      const starDisabled = isPending || isStarred
      const undoDisabled = isPending || missingUsedDate || (recoveryMode === 'used' && !stationId)
      const undoTooltip = missingUsedDate ? 'Missing used date' : 'Undo'
      const eventEpochMs = resolveEpochMs({
        epochSeconds: item.recovery?.event_at_epoch ?? undefined,
        isoString: item.recovery?.event_at_iso_utc ?? item.recovery?.event_at ?? null,
      })
      const relative = eventEpochMs ? formatRelativeTime(eventEpochMs, stationTimezone) : null
      const prefix =
        recoveryMode === 'used'
          ? 'Used'
          : recoveryMode === 'dismissed_all'
            ? 'Dismissed for everyone'
            : 'Dismissed'
      const statusLabel = relative?.label ? `${prefix} ${relative.label}` : prefix

      return (
        <Stack direction="row" spacing={0.5} alignItems="center">
          {statusLabel ? (
            <Tooltip title={relative?.tooltip ?? statusLabel} enterDelay={300} describeChild>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                {statusLabel}
              </Typography>
            </Tooltip>
          ) : null}
          {canStar ? (
            <Tooltip title={isStarred ? 'In My Prep' : 'Add to My Prep'} enterDelay={300} describeChild>
              <span>
                <IconButton
                  aria-label="Add to My Prep"
                  size="small"
                  disabled={starDisabled}
                  onClick={() => handleAddToMyPrepAndUndo(item)}
                  sx={{ p: 0.5 }}
                >
                  {isStarred ? <StarIcon color="warning" fontSize="inherit" /> : <StarBorderIcon fontSize="inherit" />}
                </IconButton>
              </span>
            </Tooltip>
          ) : null}
          <Tooltip title={undoTooltip} enterDelay={300} describeChild>
            <span>
              <IconButton
                aria-label="Undo"
                size="small"
                disabled={undoDisabled}
                onClick={() => handleUndo(item)}
                sx={{ p: 0.5, ml: 0.5 }}
              >
                <UndoIcon fontSize="inherit" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      )
    },
    [handleAddToMyPrepAndUndo, handleUndo, pendingActionIds, recoveryMode, stationId, stationTimezone],
  )

  const renderRecoveryCard = React.useCallback(
    (item: PrepListItem) => (
      <PrepCard
        item={item}
        lineClamp={lineClamp}
        isSuperadmin={false}
        onOpenDetail={() => undefined}
        onToggleMyPrep={async () => undefined}
        onDismiss={async () => undefined}
        onAddToShowClock={undefined}
        launchedFromMyPrep={false}
        actionsSlot={actionsSlot(item)}
      />
    ),
    [actionsSlot, lineClamp],
  )

  const emptyState = !itemsState.loading ? (
    <Box sx={{ p: 3 }}>
      <Typography variant="body2" color="text.secondary">
        {emptyMessage}
      </Typography>
    </Box>
  ) : null

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
      <Stack spacing={2} sx={{ p: 3, pb: 1 }}>
        <Box>
          <Typography variant="h6" component="h2">
            Used & Dismissed
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review prep items you have used or dismissed, and undo them if needed.
          </Typography>
        </Box>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={recoveryMode}
          onChange={handleModeChange}
          aria-label="Used and dismissed mode"
          sx={{
            '& .MuiToggleButton-root': {
              px: 1.25,
              fontWeight: 600,
            },
          }}
        >
          <ToggleButton value="dismissed" aria-label="Dismissed prep items">
            Dismissed
          </ToggleButton>
          <ToggleButton value="used" aria-label="Used prep items">
            Used
          </ToggleButton>
          {isSuperadmin ? (
            <ToggleButton value="dismissed_all" aria-label="Dismissed for everyone">
              Dismissed for Everyone
            </ToggleButton>
          ) : null}
        </ToggleButtonGroup>
        {itemsState.error ? <Alert severity="error">{itemsState.error}</Alert> : null}
      </Stack>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        {itemsState.loading && itemsState.items.length === 0 ? (
          <Stack spacing={2} sx={{ px: 3, pb: 3 }}>
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} variant="rounded" height={140} sx={{ borderRadius: 2 }} />
            ))}
          </Stack>
        ) : (
          <PrepListViewport>
            {({ scrollContainer }) => (
              <PrepGrid
                items={itemsState.items}
                renderItem={renderRecoveryCard}
                getKey={(item) => item.id}
                hasMore={itemsState.hasMore}
                loadingMore={itemsState.loadingMore}
                onLoadMore={loadMore}
                scrollContainer={scrollContainer}
                emptyState={emptyState}
                forceSingleColumn={forceSingleColumn}
                debugLabel="prep-recovery"
              />
            )}
          </PrepListViewport>
        )}
      </Box>
    </Box>
  )
}

export default PrepRecoveryShell
